import React, { useState, useEffect, useCallback } from 'react';
import { targetAPI, crmAPI, aiAssistantAPI } from '../../lib/api';
import { useRBAC } from '../../lib/RBACContext';
import { formatCurrencyCompact, formatNumberCompact } from '../../lib/currency';
import { AdvancedFilterBuilder, FilterChipBar } from '../layout/AdvancedFilterBuilder';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Progress } from '../ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import {
  Target, Plus, TrendingUp, Trophy, Users, Trash2, BarChart2, Activity, Phone, Mail, Calendar, Monitor, FlaskConical, Presentation, DollarSign, Calculator, ChevronDown, Crosshair, Award, FileText, AlertTriangle, CheckCircle2, Bell, User, ExternalLink, Clock, XCircle, Filter, PieChart, Layers, ArrowRight, GitBranch, X, Sparkles, Loader2, Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell, PieChart as RPieChart, Pie } from 'recharts';
import { toast } from 'sonner';

const actIcons = { Call: Phone, Email: Mail, Meeting: Calendar, Demo: Monitor, 'Proof of concept': FlaskConical, 'Work Shop': Presentation, 'Site Visit': Target, 'To Do': CheckCircle2, Task: FileText, 'Follow-up': Clock };
const actColors = { Call: '#3b82f6', Email: '#8b5cf6', Meeting: '#10b981', Demo: '#f59e0b', 'Proof of concept': '#ef4444', 'Work Shop': '#06b6d4', 'Site Visit': '#ec4899', 'To Do': '#6366f1', Task: '#84cc16' };
const SEGMENT_COLORS = ['#800000', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#ef4444', '#84cc16', '#6366f1'];

// ===== DRILLABLE POPUP =====
function DrillPopup({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <ScrollArea className="max-h-[65vh]">{children}</ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ===== CLICKABLE METRIC CARD =====
function MetricCard({ label, value, subValue, icon: Icon, color = 'text-blue-600', bgColor = 'bg-blue-50', onClick }) {
  return (
    <Card className={`hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer hover:border-[#800000]/30' : ''}`} onClick={onClick} data-testid={`metric-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-500 font-medium">{label}</span>
          <div className={`p-1.5 rounded-full ${bgColor}`}>{Icon && <Icon className={`h-3.5 w-3.5 ${color}`} />}</div>
        </div>
        <p className="text-xl font-bold text-gray-900">{value}</p>
        {subValue && <p className="text-xs text-gray-500 mt-0.5">{subValue}</p>}
        {onClick && <p className="text-[10px] text-[#800000] mt-1 flex items-center gap-0.5"><ExternalLink className="h-2.5 w-2.5" /> Click to drill down</p>}
      </CardContent>
    </Card>
  );
}

// ===== MAIN PAGE =====
export default function PerformanceHubPage() {
  const { permissions } = useRBAC();
  const [loading, setLoading] = useState(true);
  const [myData, setMyData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [pmActuals, setPmActuals] = useState([]);
  const [collection, setCollection] = useState({});
  const [collectionYear, setCollectionYear] = useState(null); // actual year shown in collection tab
  const [alertsData, setAlertsData] = useState({ alerts: [], summary: {} });
  const [productManagers, setProductManagers] = useState([]);
  const [solutionCats, setSolutionCats] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityCounts, setActivityCounts] = useState({ pending: 0, overdue: 0, completed: 0, total: 0 });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planItems, setPlanItems] = useState([]);
  const [redistributions, setRedistributions] = useState([]);
  const [selectedPlanItems, setSelectedPlanItems] = useState(new Set());
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showRedistribute, setShowRedistribute] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  // Segment state
  const [showSegmentEditor, setShowSegmentEditor] = useState(false);
  const [segments, setSegments] = useState(null);
  // AI Recommendations state
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  // Filters
  const [yearFilter, setYearFilter] = useState('2026'); // Default to current year
  const [pmFilter, setPmFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const isAdmin = permissions.includes('admin:*') || permissions.includes('system_admin');
  const isPD = myData?.is_product_director;
  const isSD = myData?.is_sales_director && !isPD;
  const isRep = myData?.is_sales_rep;
  const canManage = permissions.includes('manage_goals') || permissions.includes('admin:*');
  const showExecutiveTabs = isAdmin || isSD;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const fParams = {};
      if (yearFilter && yearFilter !== 'all') fParams.year = yearFilter;
      if (pmFilter && pmFilter !== 'all') fParams.product_manager = pmFilter;

      const calls = [targetAPI.getMyData(), targetAPI.getAlerts(),
        targetAPI.getSolutionCategories(), targetAPI.getSalespersons(), targetAPI.getActivityTypes()];
      if (canManage) {
        const planParams = {};
        if (pmFilter && pmFilter !== 'all') planParams.product_manager = pmFilter;
        if (yearFilter && yearFilter !== 'all') planParams.year = yearFilter;
        calls.push(
          targetAPI.listRevenuePlans(planParams),
          targetAPI.getActualsByPM(pmFilter && pmFilter !== 'all' ? { product_manager: pmFilter } : {}),
          targetAPI.getProductManagers()
        );
      }
      const results = await Promise.allSettled(calls);
      if (results[0].status === 'fulfilled') setMyData(results[0].value.data);
      if (results[1].status === 'fulfilled') setAlertsData(results[1].value.data);
      if (results[2]?.status === 'fulfilled') setSolutionCats(results[2].value.data);
      if (results[3]?.status === 'fulfilled') setSalespersons(results[3].value.data);
      if (results[4]?.status === 'fulfilled') setActivityTypes(results[4].value.data);
      
      // Load activity counts for the Activities tab (pending/overdue/completed)
      try {
        const actStats = await crmAPI.getActivityStats(yearFilter && yearFilter !== 'all' ? { year: yearFilter } : {});
        const stats = actStats.data || {};
        setActivityCounts({ pending: stats.pending || 0, overdue: stats.overdue || 0, completed: stats.completed || 0, total: stats.total || 0 });
      } catch { /* non-fatal */ }
      if (canManage) {
        if (results[5]?.status === 'fulfilled') setPlans(results[5].value.data);
        if (results[6]?.status === 'fulfilled') setPmActuals(results[6].value.data);
        if (results[7]?.status === 'fulfilled') setProductManagers(results[7].value.data);
      }
      try {
        const collYear = yearFilter && yearFilter !== 'all' ? yearFilter : null;
        let collRes = await crmAPI.getReceivablesStats(collYear ? { year: collYear } : {});
        let stats = collRes.data?.stats || {};
        let shownYear = collYear;
        // If current year has no invoices, fall back to the most recent year with data
        if (collYear && (stats.count_total || 0) === 0) {
          const prevYear = String(parseInt(collYear) - 1);
          const prevRes = await crmAPI.getReceivablesStats({ year: prevYear });
          const prevStats = prevRes.data?.stats || {};
          if ((prevStats.count_total || 0) > 0) {
            stats = prevStats;
            shownYear = prevYear;
          }
        }
        setCollectionYear(shownYear);
        setCollection({
          by_state: {
            paid: { count: stats.count_paid || 0, amount: stats.total_paid || 0 },
            not_paid: { count: (stats.count_total || 0) - (stats.count_paid || 0) - (stats.count_pending || 0), amount: (stats.total_invoiced || 0) - (stats.total_paid || 0) - (stats.total_pending || 0) },
            pending: { count: stats.count_pending || 0, amount: stats.total_pending || 0 },
          },
          overdue_count: stats.count_overdue || 0, overdue_amount: stats.total_overdue || 0, total_invoices: stats.count_total || 0
        });
      } catch {}
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  }, [canManage, yearFilter, pmFilter, catFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadPlanDetails = useCallback(async (planId) => {
    if (!planId) return;
    try {
      const [itemsR, redistR, segR] = await Promise.allSettled([
        targetAPI.listPlanItems(planId),
        targetAPI.listRedistributions(planId),
        targetAPI.listSegments(planId),
      ]);
      if (itemsR.status === 'fulfilled') setPlanItems(itemsR.value.data);
      if (redistR.status === 'fulfilled') setRedistributions(redistR.value.data);
      if (segR.status === 'fulfilled') setSegments(segR.value.data);
    } catch {}
  }, []);
  useEffect(() => { if (selectedPlan) loadPlanDetails(selectedPlan.id); }, [selectedPlan, loadPlanDetails]);

  const defaultTab = showExecutiveTabs ? 'cascade' : isPD ? 'myplan' : 'mytargets';
  const [tab, setTab] = useState(null);
  useEffect(() => { if (myData && !tab) setTab(defaultTab); }, [myData, defaultTab, tab]);

  // Drill handlers
  const drillOpps = async (params = {}) => {
    try {
      const p = { limit: 50, ...params };
      if (yearFilter && yearFilter !== 'all') p.year = yearFilter;
      if (pmFilter && pmFilter !== 'all') p.product_manager = pmFilter;
      if (catFilter && catFilter !== 'all') p.solution_category = catFilter;
      const res = await crmAPI.listOpportunities(p);
      const items = res.data?.items || res.data || [];
      const title = ['Opportunities', params.stage ? `(${params.stage})` : '', yearFilter && yearFilter !== 'all' ? `- ${yearFilter}` : ''].filter(Boolean).join(' ');
      setDrillData({ title, data: items, type: 'opportunities' });
    } catch { toast.error('Failed to load'); }
  };
  const drillActivities = async (status) => {
    try {
      const res = await crmAPI.listActivities({});
      let items = res.data || [];
      if (status === 'pending') items = items.filter(a => a.status === 'planned' || a.status === 'today');
      else if (status === 'completed') items = items.filter(a => a.status === 'completed');
      else if (status === 'overdue') items = items.filter(a => a.status === 'overdue');
      setDrillData({ title: `Activities - ${status || 'All'}`, data: items, type: 'activities' });
    } catch { toast.error('Failed'); }
  };
  const drillInvoices = async (state) => {
    try {
      const res = await crmAPI.listReceivables({});
      let items = res.data?.invoices || [];
      if (state) items = items.filter(i => state === 'overdue' ? (i.status_label === 'overdue' || i.is_overdue) : i.payment_state === state);
      setDrillData({ title: `Invoices - ${state || 'All'}`, data: items, type: 'invoices' });
    } catch { toast.error('Failed'); }
  };

  const fetchAiRecommendations = async () => {
    if (!selectedPlan) return;
    setLoadingRecs(true);
    try {
      const res = await aiAssistantAPI.recommendActivities({
        plan_id: selectedPlan.id,
        segment_name: segments?.segments?.[0]?.category || '',
        target_amount: selectedPlan.booking_target || selectedPlan.target_amount || 0,
        product_manager: selectedPlan.product_manager_name || '',
      });
      setAiRecommendations(res.data.recommendations);
    } catch { toast.error('Failed to get recommendations'); }
    finally { setLoadingRecs(false); }
  };

  if (loading || !myData) return <div className="space-y-4" data-testid="performance-hub-loading"><Skeleton className="h-8 w-64" /><div className="grid grid-cols-5 gap-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20" />)}</div></div>;

  return (
    <div className="space-y-5" data-testid="performance-hub-page">
      {/* Header + Filters */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Crosshair className="h-6 w-6 text-[#800000]" /> Performance Hub</h1>
          <p className="text-gray-500 text-sm">{showExecutiveTabs ? 'Cascading targets: CEO → Product Director → Sales Team' : isPD ? `Product Director: ${myData.user_name}` : `My Targets: ${myData.user_name}`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-24 h-8 text-xs" data-testid="year-filter"><SelectValue placeholder="Year" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Years</SelectItem><SelectItem value="2024">2024</SelectItem><SelectItem value="2025">2025</SelectItem><SelectItem value="2026">2026</SelectItem></SelectContent>
          </Select>
          {showExecutiveTabs && (
            <Select value={pmFilter} onValueChange={setPmFilter}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="pd-filter"><SelectValue placeholder="Product Director" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All PDs</SelectItem>{productManagers.map(pm => <SelectItem key={pm.name} value={pm.name}>{pm.name.split(' ').slice(-1)[0]}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Categories</SelectItem>{(isPD ? solutionCats.filter(c => (myData?.my_categories || []).includes(c.name)) : solutionCats).slice(0, 15).map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          {showExecutiveTabs && <Button onClick={() => setShowCreatePlan(true)} className="bg-[#800000] hover:bg-[#9a1919] text-white h-8 text-xs" data-testid="assign-target-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Assign Target</Button>}
        </div>
      </div>

      {/* Active Filter Chips */}
      <FilterChipBar
        filters={{ year: yearFilter && yearFilter !== 'all' ? yearFilter : '', productDirector: pmFilter && pmFilter !== 'all' ? pmFilter : '', solutionCategory: catFilter && catFilter !== 'all' ? catFilter : '' }}
        onClear={(key) => { if (key === 'year') setYearFilter(''); if (key === 'productDirector') setPmFilter(''); if (key === 'solutionCategory') setCatFilter(''); }}
        onClearAll={() => { setYearFilter(''); setPmFilter(''); setCatFilter(''); }}
        onOpenAdvanced={() => setShowAdvancedFilter(true)}
      />

      {/* Alert Center */}
      {alertsData.summary?.total > 0 && canManage && (
        <button onClick={() => setShowAlerts(!showAlerts)} className={`w-full p-2.5 rounded-lg border flex items-center justify-between text-left ${alertsData.summary.critical > 0 ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`} data-testid="alerts-btn">
          <div className="flex items-center gap-2"><Bell className={`h-4 w-4 ${alertsData.summary.critical > 0 ? 'text-red-500' : 'text-blue-500'}`} /><span className="text-sm font-medium">Alerts</span>
            {alertsData.summary.critical > 0 && <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-200">{alertsData.summary.critical} Critical</Badge>}
            {(alertsData.summary.medium || 0) > 0 && <Badge variant="outline" className="text-[10px] bg-yellow-100 text-yellow-700 border-yellow-200">{alertsData.summary.medium} Medium</Badge>}
          </div><ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showAlerts ? 'rotate-180' : ''}`} />
        </button>
      )}
      {showAlerts && <div className="space-y-1.5">{(alertsData.alerts || []).map((a, i) => {
        const isC = a.severity === 'critical'; const isP = a.severity === 'positive';
        return <div key={i} className={`p-2.5 rounded-lg border text-sm flex items-start gap-2 ${isC ? 'bg-red-50 border-red-200' : isP ? 'bg-emerald-50 border-emerald-200' : 'bg-yellow-50 border-yellow-200'}`}>
          {isC ? <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" /> : isP ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" /> : <Bell className="h-4 w-4 text-yellow-500 mt-0.5" />}
          <div className="flex-1"><span className={`font-semibold ${isC ? 'text-red-700' : isP ? 'text-emerald-700' : 'text-yellow-700'}`}>{a.title}</span><p className="text-xs text-gray-600">{a.message}</p></div>
          {a.metric !== undefined && <span className={`text-lg font-bold ${isC ? 'text-red-600' : isP ? 'text-emerald-600' : 'text-yellow-600'}`}>{a.metric}%</span>}
        </div>;
      })}</div>}

      {/* CEO RAG Summary */}
      {showExecutiveTabs && <CeoSummaryCard year={yearFilter} />}

      {/* Tabs */}
      <Tabs value={tab || defaultTab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          {showExecutiveTabs && <TabsTrigger value="cascade" data-testid="tab-cascade"><GitBranch className="h-3.5 w-3.5 mr-1" /> Cascade View</TabsTrigger>}
          {showExecutiveTabs && <TabsTrigger value="pm" data-testid="tab-pm">Plan Builder</TabsTrigger>}
          {showExecutiveTabs && <TabsTrigger value="sd" data-testid="tab-sd">Sales Team</TabsTrigger>}
          {(showExecutiveTabs || isPD) && <TabsTrigger value="sales-kpi" data-testid="tab-sales-kpi" className="flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> Sales Activity KPI</TabsTrigger>}
          {isPD && <TabsTrigger value="myplan">My Plan</TabsTrigger>}
          {isPD && <TabsTrigger value="myteam">My Team</TabsTrigger>}
          {isRep && <TabsTrigger value="mytargets">My Targets</TabsTrigger>}
          <TabsTrigger value="activities">Activity Summary</TabsTrigger>
          <TabsTrigger value="collection">Collection</TabsTrigger>
          {canManage && <TabsTrigger value="incentive">Incentive</TabsTrigger>}
        </TabsList>

        {/* CASCADE VIEW (CEO) — The main new view */}
        {showExecutiveTabs && <TabsContent value="cascade" className="mt-4 space-y-6" data-testid="cascade-view">
          {/* PD Revenue Plans */}
          {plans.filter(p => !p.plan_type || p.plan_type === 'revenue').length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Target className="h-4 w-4 text-[#800000]" /> Product Director Targets</h3>
              {plans.filter(p => !p.plan_type || p.plan_type === 'revenue').map(plan => (
                <CascadePlanCard key={plan.id} plan={plan} selected={selectedPlan?.id === plan.id}
                  onSelect={() => { setSelectedPlan(plan); }}
                  onSegment={() => { setSelectedPlan(plan); setShowSegmentEditor(true); }}
                  onViewPlan={() => { setSelectedPlan(plan); setTab('pm'); }}
                  onDelete={() => { targetAPI.deleteRevenuePlan(plan.id).then(() => { toast.success('Deleted'); loadData(); }); }}
                />
              ))}
            </div>
          )}

          {/* Strategy GM Plans */}
          {plans.filter(p => p.plan_type === 'strategy').length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Presentation className="h-4 w-4 text-purple-600" /> Strategy Team Targets</h3>
              {plans.filter(p => p.plan_type === 'strategy').map(plan => (
                <CascadePlanCard key={plan.id} plan={plan} selected={selectedPlan?.id === plan.id} planTypeLabel="Strategy"
                  onSelect={() => { setSelectedPlan(plan); }}
                  onSegment={() => { setSelectedPlan(plan); setShowSegmentEditor(true); }}
                  onViewPlan={() => { setSelectedPlan(plan); setTab('pm'); }}
                  onDelete={() => { targetAPI.deleteRevenuePlan(plan.id).then(() => { toast.success('Deleted'); loadData(); }); }}
                />
              ))}
            </div>
          )}

          {/* Marketing Plans */}
          {plans.filter(p => p.plan_type === 'marketing').length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><BarChart2 className="h-4 w-4 text-blue-600" /> Marketing Team Targets</h3>
              {plans.filter(p => p.plan_type === 'marketing').map(plan => (
                <CascadePlanCard key={plan.id} plan={plan} selected={selectedPlan?.id === plan.id} planTypeLabel="Marketing"
                  onSelect={() => { setSelectedPlan(plan); }}
                  onSegment={() => { setSelectedPlan(plan); setShowSegmentEditor(true); }}
                  onViewPlan={() => { setSelectedPlan(plan); setTab('pm'); }}
                  onDelete={() => { targetAPI.deleteRevenuePlan(plan.id).then(() => { toast.success('Deleted'); loadData(); }); }}
                />
              ))}
            </div>
          )}

          {plans.length === 0 && (
            <Card><CardContent className="p-12 text-center">
              <GitBranch className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-500 mb-1">No Target Plans Yet</h3>
              <p className="text-sm text-gray-400 mb-4">Start by assigning a revenue target to a Product Director, Strategy GM, or Marketing Team</p>
              <Button onClick={() => setShowCreatePlan(true)} className="bg-[#800000] hover:bg-[#9a1919] text-white">
                <Plus className="h-4 w-4 mr-1" /> Assign First Target
              </Button>
            </CardContent></Card>
          )}
        </TabsContent>}

        {/* PM PLAN BUILDER */}
        {showExecutiveTabs && <TabsContent value="pm" className="mt-4 space-y-4">
          {!selectedPlan ? <Card><CardContent className="p-8 text-center text-gray-400">Select a plan from Cascade View</CardContent></Card> : <>
            <SuggestionsCard planId={selectedPlan.id} onAccepted={() => { loadPlanDetails(selectedPlan.id); loadData(); }} />
            
            {/* Segments Section */}
            {segments && segments.segments && segments.segments.length > 0 && (
              <SegmentBreakdownCard segments={segments} onEdit={() => setShowSegmentEditor(true)} />
            )}
            
            <Card className="border-[#800000]/20"><CardContent className="p-4 flex items-center justify-between"><div><h3 className="font-semibold">{selectedPlan.name}</h3><p className="text-sm text-gray-500">PD: {selectedPlan.product_manager_name} | Booking: {formatCurrencyCompact((selectedPlan.booking_target || selectedPlan.target_amount || 0))}{selectedPlan.invoiced_target > 0 ? ` | Invoiced: OMR ${formatNumberCompact(selectedPlan.invoiced_target)}` : ''}{selectedPlan.margin_target > 0 ? ` | Margin: OMR ${formatNumberCompact(selectedPlan.margin_target)}` : ''}</p></div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowSegmentEditor(true)} className="text-xs"><Layers className="h-3.5 w-3.5 mr-1" /> Segment by Category</Button>
                <Button variant="outline" onClick={fetchAiRecommendations} disabled={loadingRecs} className="text-xs border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="ai-recommend-btn">
                  {loadingRecs ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI Suggest Activities
                </Button>
                <Button onClick={() => setShowAddItem(true)} className="bg-[#800000] hover:bg-[#9a1919] text-white text-xs"><Plus className="h-4 w-4 mr-1" /> Add Activity</Button>
              </div>
            </CardContent></Card>
            
            {/* AI Activity Recommendations */}
            {aiRecommendations && aiRecommendations.length > 0 && (
              <Card className="border-2 border-amber-200 bg-amber-50/30" data-testid="ai-recommendations-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                    <Sparkles className="h-4 w-4" /> AI-Recommended Activities
                    <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">{aiRecommendations.length} suggestions</Badge>
                    <button onClick={() => setAiRecommendations(null)} className="ml-auto text-xs text-gray-400 hover:text-gray-600">Dismiss</button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {aiRecommendations.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2.5 bg-white rounded-lg border border-amber-100">
                      <div className={`mt-0.5 px-2 py-0.5 rounded text-[9px] font-bold uppercase ${rec.priority === 'high' ? 'bg-red-100 text-red-700' : rec.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                        {rec.priority}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[9px] shrink-0">{rec.type}</Badge>
                          <p className="text-sm font-medium truncate">{rec.title}</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{rec.description}</p>
                      </div>
                      <Badge variant="outline" className={`shrink-0 text-[9px] ${rec.impact === 'high' ? 'border-emerald-300 text-emerald-700' : 'border-gray-200 text-gray-500'}`}>
                        {rec.impact} impact
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            
            <Card><CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  Activity Plan Items
                  <Badge variant="secondary">{planItems.length}</Badge>
                  {planItems.filter(i => i.assign_team === 'marketing').length > 0 && <Badge variant="outline" className="text-[10px] border-blue-200 bg-blue-50 text-blue-700">{planItems.filter(i => i.assign_team === 'marketing').length} Marketing</Badge>}
                  {planItems.filter(i => i.assign_team === 'strategy').length > 0 && <Badge variant="outline" className="text-[10px] border-purple-200 bg-purple-50 text-purple-700">{planItems.filter(i => i.assign_team === 'strategy').length} Strategy</Badge>}
                </CardTitle>
                {selectedPlanItems.size > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-medium">{selectedPlanItems.size} selected</span>
                    <Button size="sm" variant="destructive" className="h-7 text-xs gap-1" data-testid="bulk-delete-btn"
                      onClick={async () => {
                        if (!window.confirm(`Delete ${selectedPlanItems.size} item(s)?`)) return;
                        try {
                          await Promise.all([...selectedPlanItems].map(id => targetAPI.deletePlanItem(id)));
                          toast.success(`Deleted ${selectedPlanItems.size} items`);
                          setSelectedPlanItems(new Set());
                          loadPlanDetails(selectedPlan.id);
                        } catch { toast.error('Some items failed to delete'); }
                      }}>
                      <Trash2 className="h-3 w-3" /> Delete {selectedPlanItems.size}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedPlanItems(new Set())}>Clear</Button>
                  </div>
                )}
              </div>
            </CardHeader>
              <CardContent className="p-0"><Table><TableHeader><TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    checked={planItems.length > 0 && selectedPlanItems.size === planItems.length}
                    onCheckedChange={c => setSelectedPlanItems(c ? new Set(planItems.map(i => i.id)) : new Set())}
                    data-testid="select-all-items" aria-label="Select all"
                  />
                </TableHead>
                <TableHead>Activity</TableHead><TableHead>Category</TableHead><TableHead>Team</TableHead>
                <TableHead className="text-right">Target</TableHead><TableHead className="text-right">Actual</TableHead>
                <TableHead>Assigned</TableHead><TableHead className="w-24">Actions</TableHead>
              </TableRow></TableHeader>
                <TableBody>{planItems.map(item => {
                  const Icon = actIcons[item.activity_type] || Activity;
                  const assigned = item.redistributed_total || 0;
                  const matched = assigned >= (item.target_count || 0) && item.target_count > 0;
                  const remaining = (item.target_count || 0) - assigned;
                  const teamLabel = item.assign_team === 'marketing' ? 'Marketing' : item.assign_team === 'strategy' ? 'Strategy' : 'Sales';
                  const teamColor = item.assign_team === 'marketing' ? 'bg-blue-100 text-blue-700' : item.assign_team === 'strategy' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600';
                  const isSelected = selectedPlanItems.has(item.id);
                  return (
                    <TableRow key={item.id} className={isSelected ? 'bg-[#800000]/10' : 'hover:bg-gray-50/50'} data-testid={`plan-item-row-${item.id}`}>
                      <TableCell>
                        <Checkbox checked={isSelected}
                          onCheckedChange={() => setSelectedPlanItems(prev => { const n = new Set(prev); n.has(item.id) ? n.delete(item.id) : n.add(item.id); return n; })}
                          data-testid={`select-item-${item.id}`} aria-label={`Select ${item.activity_type}`} />
                      </TableCell>
                      <TableCell><div className="flex items-center gap-2"><Icon className="h-4 w-4" style={{ color: actColors[item.activity_type] }} /><span className="text-sm font-medium">{item.activity_type}</span></div></TableCell>
                      <TableCell className="text-sm text-gray-500">{item.solution_category || '-'}</TableCell>
                      <TableCell><Badge variant="secondary" className={`text-[10px] ${teamColor}`}>{teamLabel}</Badge>{item.sponsor_pd && <p className="text-[9px] text-gray-400 mt-0.5">Sponsor: {item.sponsor_pd}</p>}</TableCell>
                      <TableCell className="text-right font-semibold">{item.target_count}</TableCell>
                      <TableCell className="text-right"><span className={(item.actual_count || 0) >= (item.target_count || 0) ? 'text-emerald-600 font-semibold' : ''}>{item.actual_count || 0}</span></TableCell>
                      <TableCell><span className={matched ? 'text-emerald-600' : 'text-orange-600'}>{assigned}/{item.target_count}</span> {remaining > 0 && <span className="text-xs text-red-500 ml-1">({remaining} left)</span>}</TableCell>
                      <TableCell><div className="flex gap-1"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowRedistribute(item)}>Assign</Button><Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-500" onClick={() => targetAPI.deletePlanItem(item.id).then(() => { toast.success('Deleted'); setSelectedPlanItems(new Set()); loadPlanDetails(selectedPlan.id); })}><Trash2 className="h-3 w-3" /></Button></div></TableCell>
                    </TableRow>);
                })}{planItems.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No activity items. Add items or accept system suggestions.</TableCell></TableRow>}</TableBody></Table></CardContent></Card>
            {redistributions.length > 0 && <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Assignments ({redistributions.length})</CardTitle></CardHeader><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Activity</TableHead><TableHead>Assigned To</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Count</TableHead><TableHead>Notes</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
              <TableBody>{redistributions.map(r => (<TableRow key={r.id}><TableCell>{r.activity_type}</TableCell><TableCell className="font-medium">{r.assigned_to_name}</TableCell><TableCell><Badge variant="secondary" className="text-[10px]">{r.assign_type === 'team' ? 'Team' : 'Person'}</Badge></TableCell><TableCell className="text-right font-semibold">{r.assigned_count}</TableCell><TableCell className="text-xs text-gray-400">{r.notes || '-'}</TableCell><TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-500" onClick={() => targetAPI.deleteRedistribution(r.id).then(() => { toast.success('Removed'); loadPlanDetails(selectedPlan.id); })}><Trash2 className="h-3 w-3" /></Button></TableCell></TableRow>))}</TableBody></Table></CardContent></Card>}
          </>}
        </TabsContent>}

        {/* SALES DIRECTOR — Team Workload with PD assignments */}
        {showExecutiveTabs && <TabsContent value="sd" className="mt-4 space-y-4">
          {/* SD Summary Cards */}
          {myData?.sd_summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Team Members" value={myData.sd_summary.total_team_members} icon={Users} color="text-blue-600" bgColor="bg-blue-50" />
              <MetricCard label="Total Assigned" value={myData.sd_summary.total_assigned} icon={Target} color="text-[#800000]" bgColor="bg-red-50" />
              <MetricCard label="Total Achieved" value={myData.sd_summary.total_achieved} icon={CheckCircle2} color="text-emerald-600" bgColor="bg-emerald-50" />
              <MetricCard label="Achievement" value={`${myData.sd_summary.overall_achievement_pct}%`} icon={TrendingUp} color={myData.sd_summary.overall_achievement_pct >= 80 ? 'text-emerald-600' : myData.sd_summary.overall_achievement_pct >= 50 ? 'text-yellow-600' : 'text-red-600'} bgColor={myData.sd_summary.overall_achievement_pct >= 80 ? 'bg-emerald-50' : myData.sd_summary.overall_achievement_pct >= 50 ? 'bg-yellow-50' : 'bg-red-50'} />
            </div>
          )}

          {/* Team Workload Table */}
          {(myData?.sd_team_workload || []).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="h-4 w-4 text-[#800000]" /> PD-Assigned Team Workload</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Team Member</TableHead>
                    <TableHead>Assignments</TableHead>
                    <TableHead className="text-right">Assigned</TableHead>
                    <TableHead className="text-right">Achieved</TableHead>
                    <TableHead className="text-right">Achievement</TableHead>
                    <TableHead className="text-right">Pipeline</TableHead>
                    <TableHead className="text-right">Won</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(myData.sd_team_workload || []).map((member, idx) => (
                      <TableRow key={member.name} className="cursor-pointer hover:bg-gray-50" onClick={() => drillOpps({ sales_rep: member.name })} data-testid={`sd-team-member-${idx}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {idx === 0 ? <Trophy className="h-4 w-4 text-yellow-500" /> : <span className="text-gray-400 text-sm w-4 text-center">{idx+1}</span>}
                            <span className="font-medium text-sm">{member.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(member.assignments || []).map((a, i) => (
                              <Badge key={i} variant="secondary" className="text-[9px]">
                                {a.activity_type} x{a.assigned_count} <span className="text-gray-400 ml-0.5">({a.solution_category?.split(' ').slice(0,2).join(' ') || '-'})</span>
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{member.total_assigned}</TableCell>
                        <TableCell className="text-right"><span className={member.total_achieved >= member.total_assigned && member.total_assigned > 0 ? 'text-emerald-600 font-semibold' : ''}>{member.total_achieved}</span></TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className={`text-xs ${member.achievement_pct >= 80 ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : member.achievement_pct >= 50 ? 'border-yellow-300 text-yellow-700 bg-yellow-50' : 'border-red-300 text-red-700 bg-red-50'}`}>
                            {member.achievement_pct}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatNumberCompact(member.pipeline)}</TableCell>
                        <TableCell className="text-right text-sm">{member.won_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Salesperson Leaderboard */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Award className="h-4 w-4 text-yellow-500" /> Salesperson Performance</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table><TableHeader><TableRow><TableHead className="w-10">#</TableHead><TableHead>Salesperson</TableHead><TableHead className="text-right">Pipeline</TableHead><TableHead className="text-right">Opps</TableHead></TableRow></TableHeader>
                <TableBody>{salespersons.slice(0, 15).map((sp, idx) => (
                  <TableRow key={sp.name} className={`cursor-pointer hover:bg-gray-50 ${idx < 3 ? 'bg-gray-50/50' : ''}`} onClick={() => drillOpps({ sales_rep: sp.name })}>
                    <TableCell>{idx === 0 ? <Trophy className="h-4 w-4 text-yellow-500" /> : <span className="text-gray-400 text-sm">{idx+1}</span>}</TableCell>
                    <TableCell className="font-medium text-sm">{sp.name}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatNumberCompact(sp.total_pipeline)}</TableCell>
                    <TableCell className="text-right text-sm">{sp.opp_count}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>}

        {/* MY PLAN (PD) */}
        {isPD && <TabsContent value="myplan" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="My Opportunities" value={myData.pm_summary?.opp_count || 0} icon={Target} color="text-blue-600" bgColor="bg-blue-50" onClick={() => drillOpps({})} />
            <MetricCard label="Pipeline" value={`OMR ${(myData.pm_summary?.total_pipeline || 0).toLocaleString()}`} icon={TrendingUp} color="text-emerald-600" bgColor="bg-emerald-50" />
            <MetricCard label="Won Deals" value={myData.pm_summary?.won_count || 0} icon={Trophy} color="text-yellow-600" bgColor="bg-yellow-50" onClick={() => drillOpps({ stage: 'closed_won' })} />
            <MetricCard label="Categories" value={myData.my_categories?.length || 0} icon={BarChart2} color="text-purple-600" bgColor="bg-purple-50" />
          </div>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">My Solution Categories</CardTitle></CardHeader><CardContent><div className="flex flex-wrap gap-2">{(myData.my_categories || []).map(c => <Badge key={c} variant="secondary" className="cursor-pointer hover:bg-gray-200" onClick={() => setCatFilter(c)}>{c}</Badge>)}</div></CardContent></Card>
          {(myData.my_plans || []).map(plan => (
            <React.Fragment key={plan.id}>
            <SuggestionsCard planId={plan.id} onAccepted={loadData} />
            <Card className="border-[#800000]/20">
              <CardHeader className="pb-2"><div className="flex items-center justify-between"><CardTitle className="text-base">{plan.name} <Badge variant="outline" className="ml-2">{formatCurrencyCompact((plan.target_amount || 0))}</Badge></CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" className="text-xs" onClick={() => { setSelectedPlan(plan); setShowSegmentEditor(true); }}><Layers className="h-3.5 w-3.5 mr-1" /> Segment</Button>
                  <Button onClick={() => { setSelectedPlan(plan); setShowAddItem(true); }} className="bg-[#800000] hover:bg-[#9a1919] text-white text-xs"><Plus className="h-4 w-4 mr-1" /> Add Activity</Button>
                </div>
              </div></CardHeader>
              <CardContent>{(plan.items || []).length > 0 ? <Table><TableHeader><TableRow><TableHead>Activity</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Distributed</TableHead><TableHead className="text-right">Achieved</TableHead><TableHead>Notes</TableHead><TableHead className="w-24">Actions</TableHead></TableRow></TableHeader><TableBody>{(plan.items || []).map(item => { const remaining = (item.target_count || 0) - (item.redistributed_total || 0); const achieved = item.achieved_total || 0; const target = item.target_count || 0; const pct = target > 0 ? Math.round(achieved / target * 100) : 0; return (
                <TableRow key={item.id}><TableCell className="font-medium text-sm">{item.activity_type}</TableCell><TableCell className="text-sm text-gray-500">{item.solution_category || '-'}</TableCell><TableCell className="text-right font-semibold">{item.target_count} {remaining > 0 && <span className="text-xs text-red-500">({remaining} unassigned)</span>}</TableCell>
                  <TableCell className="text-right text-sm">{item.redistributed_total || 0}/{target}</TableCell>
                  <TableCell className="text-right"><div className="flex items-center justify-end gap-1.5"><span className={pct >= 100 ? 'text-emerald-600 font-semibold' : pct >= 50 ? 'text-yellow-600' : 'text-gray-500'}>{achieved}</span><Badge variant="outline" className={`text-[9px] ${pct >= 80 ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : pct >= 50 ? 'border-yellow-300 text-yellow-700 bg-yellow-50' : 'border-gray-200 text-gray-500'}`}>{pct}%</Badge></div></TableCell>
                  <TableCell className="text-sm text-gray-400">{item.notes || '-'}</TableCell>
                  <TableCell><div className="flex gap-1"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSelectedPlan(plan); setShowRedistribute(item); }}>Assign</Button><Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-500" onClick={() => targetAPI.deletePlanItem(item.id).then(() => { toast.success('Deleted'); loadData(); })}><Trash2 className="h-3 w-3" /></Button></div></TableCell></TableRow>); })}</TableBody></Table> : <p className="text-gray-400 text-sm py-4 text-center">No items yet. Segment your target by category, then add activity plans.</p>}</CardContent>
            </Card>
            </React.Fragment>))}
          {(myData.my_plans || []).length === 0 && <Card><CardContent className="p-8 text-center text-gray-400">No plans assigned yet.</CardContent></Card>}
        </TabsContent>}

        {/* MY TEAM (PD) — with assigned targets + achievement */}
        {isPD && <TabsContent value="myteam" className="mt-4"><Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-[#800000]" /> Salespersons on My Opportunities</CardTitle></CardHeader><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead className="w-10">#</TableHead><TableHead>Salesperson</TableHead><TableHead className="text-right">Opps</TableHead><TableHead className="text-right">Pipeline</TableHead><TableHead className="text-right">Won</TableHead><TableHead className="text-right">Assigned</TableHead><TableHead className="text-right">Achieved</TableHead></TableRow></TableHeader><TableBody>{(myData.my_salespersons || []).map((sp, idx) => { const hasTasks = (sp.assigned_targets || 0) > 0; return (
          <TableRow key={sp.name} className="cursor-pointer hover:bg-gray-50" onClick={() => drillOpps({ sales_rep: sp.name })} data-testid={`pd-team-member-${idx}`}>
            <TableCell>{idx === 0 ? <Trophy className="h-4 w-4 text-yellow-500" /> : <span className="text-gray-400">{idx+1}</span>}</TableCell>
            <TableCell className="font-medium">{sp.name}</TableCell>
            <TableCell className="text-right">{sp.opp_count}</TableCell>
            <TableCell className="text-right font-mono">{formatNumberCompact(sp.pipeline)}</TableCell>
            <TableCell className="text-right text-sm">{sp.won_count || 0}</TableCell>
            <TableCell className="text-right">{hasTasks ? <Badge variant="outline" className="text-[10px] border-[#800000]/30 text-[#800000]">{sp.assigned_targets}</Badge> : <span className="text-gray-300">-</span>}</TableCell>
            <TableCell className="text-right">{hasTasks ? <span className={sp.achieved >= sp.assigned_targets ? 'text-emerald-600 font-semibold' : 'text-gray-500'}>{sp.achieved}</span> : <span className="text-gray-300">-</span>}</TableCell>
          </TableRow>); })}</TableBody></Table></CardContent></Card></TabsContent>}

        {/* MY TARGETS (Sales Rep) */}
        {isRep && <TabsContent value="mytargets" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="My Opportunities" value={myData.my_opp_summary?.opp_count || 0} icon={Target} color="text-blue-600" bgColor="bg-blue-50" onClick={() => drillOpps({})} />
            <MetricCard label="My Pipeline" value={`OMR ${(myData.my_opp_summary?.total_pipeline || 0).toLocaleString()}`} icon={TrendingUp} color="text-emerald-600" bgColor="bg-emerald-50" />
            <MetricCard label="Won Deals" value={myData.my_opp_summary?.won_count || 0} icon={Trophy} color="text-yellow-600" bgColor="bg-yellow-50" onClick={() => drillOpps({ stage: 'closed_won' })} />
            <MetricCard label="My Accounts" value={myData.my_accounts_count || 0} icon={Users} color="text-cyan-600" bgColor="bg-cyan-50" />
          </div>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity className="h-4 w-4 text-purple-500" /> My Activities</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 md:grid-cols-4 gap-2">{(myData.my_activities || []).map(a => { const Icon = actIcons[a.type] || Activity; return <div key={a.type} className="p-3 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => drillActivities()}><div className="flex items-center gap-1.5 mb-1"><Icon className="h-3.5 w-3.5" style={{ color: actColors[a.type] || '#6b7280' }} /><span className="text-xs text-gray-500">{a.type}</span></div><p className="text-lg font-bold">{a.count}</p></div>; })}</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="h-4 w-4 text-[#800000]" /> Assigned Targets</CardTitle></CardHeader><CardContent>{(myData.my_assigned_tasks || []).length > 0 ? <Table><TableHeader><TableRow><TableHead>Activity</TableHead><TableHead>Solution</TableHead><TableHead>From PD</TableHead><TableHead className="text-right">Count</TableHead></TableRow></TableHeader><TableBody>{(myData.my_assigned_tasks || []).map(t => <TableRow key={t.id}><TableCell className="font-medium">{t.activity_type}</TableCell><TableCell className="text-gray-500">{t.solution_category || '-'}</TableCell><TableCell className="text-gray-500">{t.product_manager_name || '-'}</TableCell><TableCell className="text-right font-semibold">{t.assigned_count}</TableCell></TableRow>)}</TableBody></Table> : <p className="text-gray-400 text-sm text-center py-4">No targets assigned yet.</p>}</CardContent></Card>
        </TabsContent>}

        {/* ACTIVITIES TAB (all roles) */}
        <TabsContent value="activities" className="mt-4 space-y-4">
          <div className="rounded-lg border border-dashed bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="performance-hub-activity-summary-note">
            This is a summary view for execution status. Use the full <span className="font-semibold text-slate-800">Activities</span> module for the unified feed, cadence, and assignment detail.
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Card className="cursor-pointer hover:shadow-md hover:border-yellow-300" onClick={() => drillActivities('pending')} data-testid="activities-pending-card">
              <CardContent className="p-4 text-center">
                <Clock className="h-6 w-6 text-yellow-500 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Planned / Today</p>
                <p className="text-2xl font-bold text-yellow-600">{activityCounts.pending}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">upcoming</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md hover:border-red-300" onClick={() => drillActivities('overdue')} data-testid="activities-overdue-card">
              <CardContent className="p-4 text-center">
                <XCircle className="h-6 w-6 text-red-500 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{activityCounts.overdue}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">past due date</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md hover:border-emerald-300" onClick={() => drillActivities('completed')} data-testid="activities-completed-card">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">{activityCounts.completed}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">done</p>
              </CardContent>
            </Card>
          </div>
          <Button variant="outline" onClick={() => drillActivities('')} className="w-full">
            <Activity className="h-4 w-4 mr-1" /> Open Unified Activity Feed ({activityCounts.total})
          </Button>
        </TabsContent>

        {/* COLLECTION */}
        <TabsContent value="collection" className="mt-4 space-y-4">
          {collectionYear && collectionYear !== yearFilter && yearFilter !== 'all' && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
              <span>No invoices found for {yearFilter} — showing {collectionYear} data instead.</span>
              <button onClick={() => setYearFilter(collectionYear)} className="font-semibold underline ml-1 hover:text-amber-900">Switch to {collectionYear}</button>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(collection.by_state || {}).map(([state, data]) => (
              <Card key={state} className="cursor-pointer hover:shadow-md" onClick={() => drillInvoices(state)}><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase">{state.replace('_', ' ')}</p><p className="text-xl font-bold">{data.count} invoices</p><p className="text-sm text-gray-500 font-mono">{formatCurrencyCompact(data.amount || 0)}</p><p className="text-[10px] text-[#800000] flex items-center gap-0.5 mt-1"><ExternalLink className="h-2.5 w-2.5" /> Click to view</p></CardContent></Card>
            ))}
          </div>
          <Card className="border-red-200 cursor-pointer hover:shadow-md" onClick={() => drillInvoices('overdue')}><CardContent className="p-4 flex items-center gap-4"><div className="p-3 rounded-full bg-red-50"><AlertTriangle className="h-6 w-6 text-red-500" /></div><div><h3 className="font-semibold">Overdue Invoices</h3><p className="text-sm text-gray-500">{collection.overdue_count || 0} past due</p><p className="text-lg font-bold text-red-600 font-mono">{formatCurrencyCompact((collection.overdue_amount || 0))}</p></div></CardContent></Card>
        </TabsContent>

        {/* INCENTIVE */}
        {canManage && <TabsContent value="incentive" className="mt-4"><IncentiveSection plans={plans} /></TabsContent>}

        {/* SALES ACTIVITY KPI — Target vs Actual correlation */}
        {(showExecutiveTabs || isPD) && <TabsContent value="sales-kpi" className="mt-4"><SalesActivityKpiPanel yearFilter={yearFilter} /></TabsContent>}
      </Tabs>

      {/* DRILL POPUP */}
      <DrillPopup open={!!drillData} onClose={() => setDrillData(null)} title={drillData?.title || ''}>
        {drillData?.type === 'opportunities' && <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Account</TableHead><TableHead>Stage</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Owner</TableHead></TableRow></TableHeader><TableBody>{(drillData?.data || []).map((o, i) => <TableRow key={i}><TableCell className="font-medium text-sm">{o.name}</TableCell><TableCell className="text-sm text-gray-500">{o.account_name || '-'}</TableCell><TableCell><Badge variant="secondary" className="text-xs">{o.custom_stage || o.stage}</Badge></TableCell><TableCell className="text-right font-mono text-sm">{formatCurrencyCompact((o.sale_value || o.amount || 0))}</TableCell><TableCell className="text-sm">{o.owner_name}</TableCell></TableRow>)}{(drillData?.data || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-gray-400">No data</TableCell></TableRow>}</TableBody></Table>}
        {drillData?.type === 'activities' && <Table><TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Owner</TableHead><TableHead>Due Date</TableHead></TableRow></TableHeader><TableBody>{(drillData?.data || []).map((a, i) => <TableRow key={i}><TableCell><Badge variant="secondary" className="text-xs">{a.type_display || a.type}</Badge></TableCell><TableCell className="text-sm">{a.subject}</TableCell><TableCell><Badge variant={a.status === 'completed' ? 'default' : 'secondary'} className={`text-xs ${a.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : ''}`}>{a.status}</Badge></TableCell><TableCell className="text-sm">{a.owner_name}</TableCell><TableCell className="text-sm">{a.due_date}</TableCell></TableRow>)}{(drillData?.data || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-4 text-gray-400">No data</TableCell></TableRow>}</TableBody></Table>}
        {drillData?.type === 'invoices' && <Table><TableHeader><TableRow><TableHead>Invoice #</TableHead><TableHead>Account</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Date</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead></TableRow></TableHeader><TableBody>{(drillData?.data || []).map((inv, i) => <TableRow key={i}><TableCell className="text-sm font-mono">{inv.invoice_number}</TableCell><TableCell className="text-sm">{inv.account || inv.account_name}</TableCell><TableCell className="text-right font-mono text-sm">{formatCurrencyCompact((inv.amount || inv.amount_total || 0))}</TableCell><TableCell className="text-sm">{inv.invoice_date}</TableCell><TableCell className="text-sm">{inv.due_date}</TableCell><TableCell><Badge variant="secondary" className={`text-xs ${inv.status_label === 'overdue' || inv.is_overdue ? 'bg-red-100 text-red-700' : inv.payment_state === 'paid' ? 'bg-emerald-100 text-emerald-700' : ''}`}>{inv.status_label || inv.payment_state}</Badge></TableCell></TableRow>)}{(drillData?.data || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-4 text-gray-400">No data</TableCell></TableRow>}</TableBody></Table>}
      </DrillPopup>

      {/* DIALOGS */}
      {showExecutiveTabs && <CreatePlanDialog open={showCreatePlan} onClose={() => setShowCreatePlan(false)} onCreated={(newPlan) => { loadData().then(() => { setShowCreatePlan(false); if (newPlan) { setSelectedPlan(newPlan); setTab('pm'); } }); }} productManagers={productManagers} />}
      {selectedPlan && <AddItemDialog open={showAddItem} onClose={() => setShowAddItem(false)} onCreated={() => { if (selectedPlan) loadPlanDetails(selectedPlan.id); loadData(); setShowAddItem(false); }} planId={selectedPlan.id} solutionCats={isPD ? solutionCats.filter(c => (myData?.my_categories || []).includes(c.name)) : solutionCats} activityTypes={activityTypes} />}
      {showRedistribute && <RedistributeDialog open={!!showRedistribute} onClose={() => setShowRedistribute(null)} onCreated={() => { if (selectedPlan) loadPlanDetails(selectedPlan.id); loadData(); setShowRedistribute(null); }} item={showRedistribute} />}
      {selectedPlan && <SegmentEditorDialog open={showSegmentEditor} onClose={() => setShowSegmentEditor(false)} plan={selectedPlan} solutionCats={solutionCats} onSaved={() => { loadPlanDetails(selectedPlan.id); loadData(); setShowSegmentEditor(false); }} />}
      <AdvancedFilterBuilder open={showAdvancedFilter} onClose={() => setShowAdvancedFilter(false)}
        currentFilters={{ year: yearFilter, productDirector: pmFilter, solutionCategory: catFilter }}
        filterOptions={{ productDirectors: productManagers.map(p => p.name), solutionCategories: solutionCats.map(c => c.name), salespersons: salespersons.map(s => s.name) }}
        onApply={(filters) => {
          if (filters.year) setYearFilter(filters.year);
          if (filters.productDirector) setPmFilter(filters.productDirector);
          if (filters.solutionCategory) setCatFilter(filters.solutionCategory);
          if (filters.quarter) setYearFilter('');
        }}
      />
    </div>
  );
}

// ===== CASCADE PLAN CARD (shows the visual flow: CEO → PD → Categories → Activities) =====
function CascadePlanCard({ plan, selected, onSelect, onSegment, onViewPlan, onDelete, planTypeLabel }) {
  const bookingTarget = plan.booking_target || plan.target_amount || 0;
  const bookingPct = plan.booking_pct || 0;
  const invoicedPct = plan.invoiced_pct || 0;
  const marginPct = plan.margin_pct || 0;
  const isSegmented = plan.segmented || plan.segment_count > 0;
  const pType = plan.plan_type || 'revenue';
  const typeColor = pType === 'strategy' ? 'purple' : pType === 'marketing' ? 'blue' : 'gray';

  return (
    <Card className={`transition-all ${selected ? 'border-[#800000] shadow-md' : 'hover:border-gray-300'}`} data-testid={`cascade-card-${plan.id}`}>
      <CardContent className="p-4">
        {/* Plan Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="cursor-pointer" onClick={onSelect}>
            <h3 className="font-semibold text-base text-gray-900 flex items-center gap-2">
              {plan.name}
              {planTypeLabel && <Badge variant="outline" className={`text-[10px] border-${typeColor}-200 bg-${typeColor}-50 text-${typeColor}-700`}>{planTypeLabel}</Badge>}
            </h3>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <User className="h-3 w-3" /> {plan.product_manager_name}
              <span className="text-gray-300 mx-1">|</span>
              {plan.period}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {!isSegmented && (
              <Button size="sm" variant="outline" className="h-7 text-xs border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100" onClick={onSegment}>
                <Layers className="h-3 w-3 mr-1" /> Segment
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onViewPlan}>
              <ArrowRight className="h-3 w-3 mr-1" /> Plan Builder
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-500" onClick={onDelete}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Triple Target Progress */}
        <div className="grid grid-cols-3 gap-4 mb-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-medium text-gray-500 uppercase">Booking</span>
              <span className="text-xs font-bold text-gray-900">{bookingPct}%</span>
            </div>
            <Progress value={Math.min(bookingPct, 100)} className="h-2" />
            <div className="flex justify-between mt-0.5">
              <span className="text-[10px] text-gray-400">{formatCurrencyCompact((plan.actual_booking || 0))}</span>
              <span className="text-[10px] text-gray-400">/ {bookingTarget.toLocaleString()}</span>
            </div>
          </div>
          {plan.invoiced_target > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-medium text-gray-500 uppercase">Invoiced</span>
                <span className="text-xs font-bold text-gray-900">{invoicedPct}%</span>
              </div>
              <Progress value={Math.min(invoicedPct, 100)} className="h-2" />
              <div className="flex justify-between mt-0.5">
                <span className="text-[10px] text-gray-400">{formatCurrencyCompact((plan.actual_invoiced || 0))}</span>
                <span className="text-[10px] text-gray-400">/ {formatNumberCompact(plan.invoiced_target)}</span>
              </div>
            </div>
          )}
          {plan.margin_target > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-medium text-gray-500 uppercase">Margin</span>
                <span className="text-xs font-bold text-gray-900">{marginPct}%</span>
              </div>
              <Progress value={Math.min(marginPct, 100)} className="h-2" />
              <div className="flex justify-between mt-0.5">
                <span className="text-[10px] text-gray-400">{formatCurrencyCompact((plan.actual_margin || 0))}</span>
                <span className="text-[10px] text-gray-400">/ {formatNumberCompact(plan.margin_target)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Status Badges */}
        <div className="flex items-center gap-2 text-[10px]">
          <Badge variant="secondary" className="text-[10px]">{plan.total_opps || 0} open opps</Badge>
          <Badge variant="secondary" className="text-[10px]">Pipeline: {formatCurrencyCompact((plan.total_pipeline || 0))}</Badge>
          {isSegmented && <Badge variant="outline" className="text-[10px] border-green-200 bg-green-50 text-green-700"><Layers className="h-2.5 w-2.5 mr-0.5" /> {plan.segment_count || '?'} categories</Badge>}
          <Badge variant="secondary" className="text-[10px]">{plan.activity_items_count || 0} activities</Badge>
          {plan.won_deals > 0 && <Badge variant="outline" className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700">{plan.won_deals} won</Badge>}
        </div>
      </CardContent>
    </Card>
  );
}

// ===== SEGMENT BREAKDOWN CARD (visual category breakdown) =====
function SegmentBreakdownCard({ segments, onEdit }) {
  const segList = segments?.segments || [];
  const totals = segments?.totals || {};
  const planTargets = segments?.plan_targets || {};
  const isBalanced = segments?.is_balanced;

  if (segList.length === 0) return null;

  const chartData = segList.map((s, i) => ({
    name: s.solution_category,
    value: s.booking_target,
    fill: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
  }));

  return (
    <Card className="border-[#800000]/10" data-testid="segment-breakdown">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Layers className="h-4 w-4 text-[#800000]" /> Category Segmentation
            {isBalanced ? <Badge variant="outline" className="text-[10px] border-green-200 bg-green-50 text-green-700"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Balanced</Badge>
              : <Badge variant="outline" className="text-[10px] border-red-200 bg-red-50 text-red-700"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Unbalanced</Badge>}
          </CardTitle>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onEdit}>Edit Segments</Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Chart */}
          <div className="h-48">
            <ResponsiveContainer>
              <RPieChart>
                <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name.substring(0, 10)} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={9}>
                  {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <RTooltip formatter={(v) => `OMR ${v.toLocaleString()}`} />
              </RPieChart>
            </ResponsiveContainer>
          </div>
          {/* Table */}
          <div className="lg:col-span-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Booking Target</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Pipeline</TableHead>
                  <TableHead className="text-right">Coverage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {segList.map((seg, i) => (
                  <TableRow key={seg.id || i}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }} />
                        <span className="text-sm font-medium">{seg.solution_category}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrencyCompact((seg.booking_target || 0))}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono text-sm ${seg.booking_pct >= 80 ? 'text-emerald-600' : seg.booking_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {seg.booking_pct || 0}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-gray-500">{formatCurrencyCompact((seg.pipeline || 0))}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary" className={`text-[10px] ${(seg.coverage_ratio || 0) >= 3 ? 'bg-emerald-100 text-emerald-700' : (seg.coverage_ratio || 0) >= 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {seg.coverage_ratio || 0}x
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-gray-50 font-semibold">
                  <TableCell className="text-sm">Total</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatCurrencyCompact((totals.booking_target || 0))}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatCurrencyCompact((totals.actual_booking || 0))}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatCurrencyCompact((totals.pipeline || 0))}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ===== SEGMENT EDITOR DIALOG =====
function SegmentEditorDialog({ open, onClose, plan, solutionCats, onSaved }) {
  const bookingTarget = plan?.booking_target || plan?.target_amount || 0;
  const invoicedTarget = plan?.invoiced_target || 0;
  const marginTarget = plan?.margin_target || 0;
  
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Load existing segments or initialize from categories
  useEffect(() => {
    if (!open || !plan) return;
    setLoadingExisting(true);
    targetAPI.listSegments(plan.id).then(res => {
      const existing = res.data?.segments || [];
      if (existing.length > 0) {
        setRows(existing.map(s => ({
          solution_category: s.solution_category,
          booking_target: s.booking_target || 0,
          invoiced_target: s.invoiced_target || 0,
          margin_target: s.margin_target || 0,
          notes: s.notes || '',
        })));
      } else {
        // Initialize from solution categories
        const cats = solutionCats.slice(0, 8);
        if (cats.length > 0) {
          const weight = 1 / cats.length;
          setRows(cats.map(c => ({
            solution_category: c.name,
            booking_target: Math.round(bookingTarget * weight),
            invoiced_target: Math.round(invoicedTarget * weight),
            margin_target: Math.round(marginTarget * weight),
            notes: '',
          })));
        }
      }
    }).catch(() => {}).finally(() => setLoadingExisting(false));
  }, [open, plan, solutionCats, bookingTarget, invoicedTarget, marginTarget]);

  const sumBooking = rows.reduce((s, r) => s + (r.booking_target || 0), 0);
  const sumInvoiced = rows.reduce((s, r) => s + (r.invoiced_target || 0), 0);
  const sumMargin = rows.reduce((s, r) => s + (r.margin_target || 0), 0);
  const bookingDiff = sumBooking - bookingTarget;
  const invoicedDiff = invoicedTarget > 0 ? sumInvoiced - invoicedTarget : 0;
  const marginDiff = marginTarget > 0 ? sumMargin - marginTarget : 0;
  const isValid = Math.abs(bookingDiff) < 2 && Math.abs(invoicedDiff) < 2 && Math.abs(marginDiff) < 2;

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addRow = () => {
    setRows(prev => [...prev, { solution_category: '', booking_target: 0, invoiced_target: 0, margin_target: 0, notes: '' }]);
  };

  const removeRow = (idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const autoBalance = () => {
    if (rows.length === 0) return;
    const remaining_booking = bookingTarget - rows.slice(0, -1).reduce((s, r) => s + (r.booking_target || 0), 0);
    const remaining_invoiced = invoicedTarget - rows.slice(0, -1).reduce((s, r) => s + (r.invoiced_target || 0), 0);
    const remaining_margin = marginTarget - rows.slice(0, -1).reduce((s, r) => s + (r.margin_target || 0), 0);
    setRows(prev => prev.map((r, i) => i === prev.length - 1 ? {
      ...r,
      booking_target: Math.max(0, Math.round(remaining_booking)),
      invoiced_target: Math.max(0, Math.round(remaining_invoiced)),
      margin_target: Math.max(0, Math.round(remaining_margin)),
    } : r));
  };

  const handleSave = async () => {
    const validRows = rows.filter(r => r.solution_category && r.booking_target > 0);
    if (validRows.length === 0) { toast.error('Add at least one category'); return; }
    if (!isValid) { toast.error('Category totals must match plan target'); return; }
    setSaving(true);
    try {
      await targetAPI.saveSegments(plan.id, validRows);
      toast.success(`${validRows.length} category segments saved`);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save segments');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh]" data-testid="segment-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-[#800000]" />
            Segment by Solution Category
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            Break down <strong>{plan?.product_manager_name}</strong>'s target into solution categories.
            Total must match: Booking {formatCurrencyCompact(bookingTarget)}
            {invoicedTarget > 0 ? ` | Invoiced OMR ${invoicedTarget.toLocaleString()}` : ''}
            {marginTarget > 0 ? ` | Margin OMR ${marginTarget.toLocaleString()}` : ''}
          </p>
        </DialogHeader>

        {loadingExisting ? <div className="p-8 text-center"><Skeleton className="h-32 w-full" /></div> : (
          <ScrollArea className="max-h-[55vh]">
            <div className="space-y-2">
              {rows.map((row, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: SEGMENT_COLORS[idx % SEGMENT_COLORS.length] }} />
                  <div className="flex-1 grid grid-cols-5 gap-2">
                    <div>
                      <Label className="text-[10px] text-gray-400">Category</Label>
                      <Select value={row.solution_category} onValueChange={v => updateRow(idx, 'solution_category', v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{solutionCats.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-400">Booking (OMR)</Label>
                      <Input type="number" value={row.booking_target} onChange={e => updateRow(idx, 'booking_target', parseFloat(e.target.value) || 0)} className="h-8 text-xs" />
                    </div>
                    {invoicedTarget > 0 && <div>
                      <Label className="text-[10px] text-gray-400">Invoiced (OMR)</Label>
                      <Input type="number" value={row.invoiced_target} onChange={e => updateRow(idx, 'invoiced_target', parseFloat(e.target.value) || 0)} className="h-8 text-xs" />
                    </div>}
                    {marginTarget > 0 && <div>
                      <Label className="text-[10px] text-gray-400">Margin (OMR)</Label>
                      <Input type="number" value={row.margin_target} onChange={e => updateRow(idx, 'margin_target', parseFloat(e.target.value) || 0)} className="h-8 text-xs" />
                    </div>}
                    <div>
                      <Label className="text-[10px] text-gray-400">Notes</Label>
                      <Input value={row.notes} onChange={e => updateRow(idx, 'notes', e.target.value)} className="h-8 text-xs" placeholder="Optional" />
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-500 shrink-0" onClick={() => removeRow(idx)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Validation summary */}
        <div className={`p-3 rounded-lg border ${isValid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center justify-between text-sm">
            <div className="space-y-0.5">
              <div className="flex items-center gap-4">
                <span className="text-gray-600">Booking: <span className={`font-bold ${Math.abs(bookingDiff) < 2 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrencyCompact(sumBooking)}</span> / {bookingTarget.toLocaleString()}</span>
                {bookingDiff !== 0 && <span className="text-xs text-red-500">({bookingDiff > 0 ? '+' : ''}{bookingDiff.toLocaleString()})</span>}
              </div>
              {invoicedTarget > 0 && <div className="flex items-center gap-4">
                <span className="text-gray-600">Invoiced: <span className={`font-bold ${Math.abs(invoicedDiff) < 2 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrencyCompact(sumInvoiced)}</span> / {invoicedTarget.toLocaleString()}</span>
              </div>}
              {marginTarget > 0 && <div className="flex items-center gap-4">
                <span className="text-gray-600">Margin: <span className={`font-bold ${Math.abs(marginDiff) < 2 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrencyCompact(sumMargin)}</span> / {marginTarget.toLocaleString()}</span>
              </div>}
            </div>
            {isValid ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <AlertTriangle className="h-5 w-5 text-red-500" />}
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={addRow} className="text-xs"><Plus className="h-3 w-3 mr-1" /> Add Category</Button>
            <Button variant="outline" size="sm" onClick={autoBalance} className="text-xs">Auto-Balance Last Row</Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !isValid} className="bg-[#800000] hover:bg-[#9a1919] text-white" data-testid="save-segments-btn">
              {saving ? 'Saving...' : `Save ${rows.filter(r => r.solution_category).length} Segments`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== SALES ACTIVITY KPI PANEL =====
const SALES_ACT_TYPES = new Set(["Demo", "RFP Submission", "Proof of concept", "Vendor Meeting", "Product Presentation", "POC", "Vendor Presentation"]);
const SAT_COLORS = { Demo: '#3b82f6', 'Proof of concept': '#06b6d4', 'RFP Submission': '#f43f5e', 'Vendor Meeting': '#14b8a6', 'Product Presentation': '#8b5cf6', POC: '#06b6d4', 'Vendor Presentation': '#7c3aed' };

function SalesActivityKpiPanel({ yearFilter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearSel, setYearSel] = useState(yearFilter || '');
  const [expandedPerson, setExpandedPerson] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (yearSel) params.year = yearSel;
    targetAPI.getSalesActivityKpi(params)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load sales activity KPI'))
      .finally(() => setLoading(false));
  }, [yearSel]);

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>;
  if (!data) return null;

  const { summary, assignments, unassigned_actuals, by_type } = data;
  const hasAssignments = assignments.length > 0;

  // Group unassigned by person for leaderboard view
  const byPerson = {};
  for (const row of [...assignments, ...unassigned_actuals]) {
    const p = row.salesperson;
    if (!byPerson[p]) byPerson[p] = { name: p, rows: [], total_actual: 0, total_target: 0 };
    byPerson[p].rows.push(row);
    byPerson[p].total_actual += row.actual_count;
    byPerson[p].total_target += row.target_count;
  }
  const leaderboard = Object.values(byPerson).sort((a, b) => b.total_actual - a.total_actual);

  return (
    <div className="space-y-5" data-testid="sales-activity-kpi">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#800000]" /> Sales Activity KPI
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Correlates assigned targets with actual completed sales activities from Odoo
            {!hasAssignments && <span className="ml-1 text-amber-600">— No assignments yet, showing actuals only</span>}
          </p>
        </div>
        <Select value={yearSel || 'all'} onValueChange={v => setYearSel(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[100px] h-8 text-xs"><SelectValue placeholder="Year" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {['2026', '2025', '2024'].map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-[#800000]/5 border-[#800000]/20">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-[#800000]">{by_type.reduce((s, t) => s + t.actual_total, 0)}</p>
            <p className="text-xs text-gray-500">Total Completed</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{leaderboard.length}</p>
            <p className="text-xs text-gray-500">Active Salespeople</p>
          </CardContent>
        </Card>
        {hasAssignments ? (
          <>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-emerald-700">{summary.achieved_count}</p>
                <p className="text-xs text-gray-500">Targets Achieved</p>
              </CardContent>
            </Card>
            <Card className={`${summary.behind_count > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${summary.behind_count > 0 ? 'text-red-700' : 'text-gray-500'}`}>{summary.behind_count}</p>
                <p className="text-xs text-gray-500">Behind Target</p>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card className="bg-amber-50 border-amber-200 md:col-span-2">
            <CardContent className="p-3 text-center">
              <p className="text-sm font-medium text-amber-700 flex items-center justify-center gap-1.5">
                <AlertTriangle className="h-4 w-4" /> No formal targets assigned yet
              </p>
              <p className="text-xs text-amber-600 mt-0.5">Go to Plan Builder → Add items → click "Assign" to set targets for each salesperson</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* By type breakdown */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart2 className="h-4 w-4 text-[#800000]" /> Activity Type Breakdown</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-50">
            {by_type.filter(t => SALES_ACT_TYPES.has(t.activity_type)).map(t => {
              const pct = t.target_total > 0 ? Math.min(100, t.actual_total / t.target_total * 100) : 100;
              const color = SAT_COLORS[t.activity_type] || '#800000';
              return (
                <div key={t.activity_type} className="flex items-center gap-3 px-4 py-3">
                  <Zap className="h-3.5 w-3.5 shrink-0" style={{ color }} />
                  <div className="w-32 shrink-0">
                    <span className="text-xs font-medium text-gray-800">{t.activity_type}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                      <span className="text-xs font-bold text-gray-900 w-6 text-right">{t.actual_total}</span>
                      {t.target_total > 0 && <span className="text-xs text-gray-400">/{t.target_total}</span>}
                    </div>
                  </div>
                  {t.target_total > 0 && (
                    <Badge className={`text-[10px] ${t.achievement_pct >= 100 ? 'bg-emerald-100 text-emerald-700' : t.achievement_pct >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'} border-0 w-12 text-center`}>
                      {t.achievement_pct}%
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard by salesperson */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Trophy className="h-4 w-4 text-yellow-500" /> Salesperson Leaderboard
            <Badge variant="secondary" className="text-[10px]">{leaderboard.length} people</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-50">
            {leaderboard.map((person, idx) => {
              const isExpanded = expandedPerson === person.name;
              const overallPct = person.total_target > 0 ? Math.round(person.total_actual / person.total_target * 100) : null;
              return (
                <div key={person.name}>
                  <div
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/50 transition-colors ${isExpanded ? 'bg-gray-50' : ''}`}
                    onClick={() => setExpandedPerson(isExpanded ? null : person.name)}
                    data-testid={`kpi-person-${person.name.replace(/\s/g, '-')}`}
                  >
                    <div className="w-7 shrink-0 text-center">
                      {idx === 0 ? <Trophy className="h-4 w-4 text-yellow-500 mx-auto" /> : <span className="text-xs text-gray-400">{idx + 1}</span>}
                    </div>
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#800000] to-rose-400 flex items-center justify-center text-white text-[9px] font-bold shrink-0">
                      {person.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{person.name}</p>
                      <div className="flex items-center gap-1 flex-wrap mt-0.5">
                        {person.rows.map(r => (
                          <span key={r.activity_type} className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: (SAT_COLORS[r.activity_type] || '#800000') + '15', color: SAT_COLORS[r.activity_type] || '#800000' }}>
                            {r.activity_type}: <strong>{r.actual_count}</strong>{r.target_count > 0 && <span className="text-gray-400">/{r.target_count}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-gray-900">{person.total_actual}</p>
                      {overallPct !== null && (
                        <Badge className={`text-[9px] ${overallPct >= 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'} border-0`}>{overallPct}%</Badge>
                      )}
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>

                  {/* Expanded: recent activities */}
                  {isExpanded && (
                    <div className="px-4 pb-3 bg-gray-50/50">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Recent Sales Activities</p>
                      <div className="space-y-1.5">
                        {person.rows.flatMap(r => r.recent_activities || []).sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 6).map((act, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <span className="text-gray-400 font-mono shrink-0">{act.date}</span>
                            <span className="text-gray-700 truncate">{act.opportunity && <span className="text-[#800000] font-medium">{act.opportunity} — </span>}{act.body || '—'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {hasAssignments && assignments.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="h-4 w-4 text-[#800000]" /> Target vs Actual Details</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Salesperson</TableHead><TableHead>Activity</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Remaining</TableHead><TableHead>Progress</TableHead><TableHead>Status</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {assignments.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium text-sm">{a.salesperson}</TableCell>
                    <TableCell><div className="flex items-center gap-1"><Zap className="h-3 w-3 text-[#800000]" /><span className="text-sm">{a.activity_type}</span></div></TableCell>
                    <TableCell className="text-right font-semibold">{a.target_count}</TableCell>
                    <TableCell className="text-right"><span className={a.actual_count >= a.target_count ? 'text-emerald-600 font-bold' : 'text-gray-700'}>{a.actual_count}</span></TableCell>
                    <TableCell className="text-right">{a.remaining > 0 ? <span className="text-red-500">{a.remaining}</span> : <span className="text-emerald-600">✓</span>}</TableCell>
                    <TableCell><div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#800000] rounded-full" style={{ width: `${Math.min(100, a.achievement_pct || 0)}%` }} /></div></TableCell>
                    <TableCell><Badge className={`text-[10px] ${a.status === 'achieved' ? 'bg-emerald-100 text-emerald-700' : a.status === 'on_track' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'} border-0`}>{a.achievement_pct}%</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ===== ASSIGNEE INPUT (autocomplete with HOD/Team detection) =====
function AssigneeInput({ value, onChange, teamType }) {
  const [allUsers, setAllUsers] = useState([]);
  const [inputVal, setInputVal] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignMode, setAssignMode] = useState(null); // null, 'team', 'individual'

  useEffect(() => {
    targetAPI.getEmployees({ active_only: true })
      .then(r => {
        const emps = r.data || [];
        // Build manager lookup: who has direct reports?
        const managerIds = new Set();
        emps.forEach(e => { if (e.manager_id) managerIds.add(String(e.manager_id)); });
        // Annotate each employee
        const enriched = emps.map(e => ({
          ...e,
          isManager: managerIds.has(String(e.source_record_id)),
          directReports: emps.filter(r => String(r.manager_id) === String(e.source_record_id)),
        }));
        setAllUsers(enriched);
      })
      .catch(() => {});
  }, []);

  const filtered = inputVal.length > 0
    ? allUsers.filter(u => u.name && u.name.toLowerCase().includes(inputVal.toLowerCase())).slice(0, 8)
    : allUsers.slice(0, 8);

  const selectUser = (emp) => {
    setInputVal(emp.name);
    setSelectedUser(emp);
    setShowDropdown(false);
    if (emp.isManager && emp.directReports.length > 0) {
      // HOD detected — show team vs individual choice
      setAssignMode(null); // reset choice
    } else {
      // Not a manager — assign individually
      setAssignMode('individual');
      onChange(emp.name, 'individual', null);
    }
  };

  const chooseMode = (mode) => {
    setAssignMode(mode);
    if (mode === 'team') {
      onChange(selectedUser.name, 'team', selectedUser.directReports.map(r => r.name));
    } else {
      onChange(selectedUser.name, 'individual', null);
    }
  };

  return (
    <div className="relative space-y-2">
      <Input value={inputVal}
        onChange={e => { setInputVal(e.target.value); setSelectedUser(null); setAssignMode(null); setShowDropdown(true); }}
        onFocus={() => setShowDropdown(true)}
        placeholder="Start typing a name..."
        className="h-9 text-sm" data-testid="assignee-input"
        autoComplete="off" />
      
      {/* Autocomplete dropdown */}
      {showDropdown && filtered.length > 0 && (
        <div className="absolute z-50 top-10 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map(emp => (
            <button key={emp.name || emp.canonical_id} type="button"
              onClick={() => selectUser(emp)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between border-b border-gray-50 last:border-0 ${inputVal === emp.name ? 'bg-[#800000]/5 text-[#800000]' : 'text-gray-700'}`}>
              <div className="flex items-center gap-2">
                {emp.isManager && <Users className="h-3 w-3 text-purple-500 shrink-0" />}
                <span className="font-medium">{emp.name}</span>
              </div>
              <span className="text-[10px] text-gray-400 ml-2 shrink-0">
                {emp.job_title || emp.department_name || ''}
                {emp.isManager ? ` · ${emp.directReports.length} reports` : ''}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* HOD detected — Team vs Individual choice */}
      {selectedUser && selectedUser.isManager && selectedUser.directReports.length > 0 && (
        <div className="p-3 rounded-lg border border-purple-200 bg-purple-50/50 space-y-2">
          <p className="text-xs font-medium text-purple-800">
            {selectedUser.name} is a department head with {selectedUser.directReports.length} team member(s)
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => chooseMode('team')}
              className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${assignMode === 'team' ? 'bg-purple-600 text-white border-purple-600' : 'border-purple-200 text-purple-700 hover:bg-purple-100'}`}
              data-testid="assign-team-btn">
              <Users className="h-3.5 w-3.5 inline mr-1" />
              Assign to {selectedUser.name.split(' ')[0]}'s Team
            </button>
            <button type="button" onClick={() => chooseMode('individual')}
              className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${assignMode === 'individual' ? 'bg-[#800000] text-white border-[#800000]' : 'border-gray-200 text-gray-700 hover:bg-gray-100'}`}
              data-testid="assign-individual-btn">
              <User className="h-3.5 w-3.5 inline mr-1" />
              Assign to {selectedUser.name.split(' ')[0]} individually
            </button>
          </div>
          {assignMode === 'team' && (
            <div className="mt-1 text-[10px] text-purple-600">
              <p className="font-medium mb-0.5">Team members who will receive cascaded targets:</p>
              {selectedUser.directReports.map(r => (
                <p key={r.name} className="text-purple-500">• {r.name} — {r.job_title || 'Member'}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Clear button */}
      {inputVal && !showDropdown && !selectedUser?.isManager && (
        <button type="button" onClick={() => { setInputVal(''); setSelectedUser(null); setAssignMode(null); }} className="absolute right-2 top-[18px] -translate-y-1/2 text-gray-400 hover:text-gray-600">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ===== DIALOGS =====
function IncentiveSection({ plans }) {
  const [sel, setSel] = useState('');
  const [result, setResult] = useState(null);
  const [calc, setCalc] = useState(false);
  const [revW, setRevW] = useState(50);
  const [actW, setActW] = useState(30);
  const [colW, setColW] = useState(20);

  const run = async () => {
    if (!sel) return;
    setCalc(true);
    try {
      const r = await targetAPI.calculateMultiVectorIncentive({
        plan_id: sel, revenue_weight: revW, activity_weight: actW, collection_weight: colW
      });
      setResult(r.data);
    } catch { toast.error('Failed'); }
    finally { setCalc(false); }
  };

  const margin = result?.so_margin_summary;
  const soMarginVec = result?.vectors?.so_margin;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="h-4 w-4 text-[#800000]" /> Multi-Vector Incentive Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Empty state guidance */}
        {!sel && (
          <div className="rounded-lg border border-dashed border-[#800000]/30 bg-[#800000]/5 p-4 text-center">
            <Calculator className="h-8 w-8 mx-auto text-[#800000]/40 mb-2" />
            <p className="text-sm font-medium text-[#800000]">Calculate Incentive Payout</p>
            <p className="text-xs text-muted-foreground mt-1 mb-3">Select a revenue plan below and set the weight for each performance vector (Revenue, Activity, Collection). Then click <strong>Calculate</strong> to see the composite score and estimated payout.</p>
            <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
              <div className="p-2 bg-white rounded border"><p className="font-semibold text-blue-600">Revenue %</p><p>Deals closed vs target</p></div>
              <div className="p-2 bg-white rounded border"><p className="font-semibold text-purple-600">Activity %</p><p>Sales activities completed</p></div>
              <div className="p-2 bg-white rounded border"><p className="font-semibold text-emerald-600">Collection %</p><p>Invoices paid on time</p></div>
            </div>
          </div>
        )}
        {/* Plan selector + weights */}
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-48">
            <Label className="text-xs">Revenue Plan</Label>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
              <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <label className="text-gray-500">Rev%</label>
            <input type="number" value={revW} onChange={e=>setRevW(+e.target.value)} className="w-14 border rounded px-1.5 py-1 text-xs" />
            <label className="text-gray-500">Act%</label>
            <input type="number" value={actW} onChange={e=>setActW(+e.target.value)} className="w-14 border rounded px-1.5 py-1 text-xs" />
            <label className="text-gray-500">Col%</label>
            <input type="number" value={colW} onChange={e=>setColW(+e.target.value)} className="w-14 border rounded px-1.5 py-1 text-xs" />
          </div>
          <Button onClick={run} disabled={calc} className="bg-[#800000] hover:bg-[#9a1919] text-white">
            <Calculator className="h-4 w-4 mr-1" /> Calculate
          </Button>
        </div>

        {result && (
          <>
            {/* Main vectors */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-gray-50 text-center">
                <p className="text-[10px] text-gray-400">Composite Score</p>
                <p className="text-2xl font-bold">{result.composite_score}</p>
                <Badge className="mt-1 text-[10px]">{result.tier}</Badge>
              </div>
              <div className="p-3 rounded-lg bg-blue-50">
                <p className="text-[10px] text-blue-500">Revenue ({revW}%)</p>
                <p className="text-lg font-bold text-blue-700">{result.vectors.revenue.achievement_pct}%</p>
                <p className="text-[10px] text-gray-400">Won: {formatCurrencyCompact((result.vectors.revenue.actual||0))}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-50">
                <p className="text-[10px] text-purple-500">Activity ({actW}%)</p>
                <p className="text-lg font-bold text-purple-700">{result.vectors.activity.achievement_pct}%</p>
                <p className="text-[10px] text-gray-400">{result.vectors.activity.total_actual}/{result.vectors.activity.total_target}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-50">
                <p className="text-[10px] text-emerald-600">Payout</p>
                <p className="text-lg font-bold text-emerald-700">{formatCurrencyCompact((result.calculated_payout||0))}</p>
                <p className="text-[10px] text-gray-400">x{result.multiplier}</p>
              </div>
            </div>

            {/* SO Margin Panel — new 4th vector (informational) */}
            {margin && (
              <div className="rounded-lg border border-[#800000]/20 bg-[#800000]/5 p-3">
                <p className="text-xs font-semibold text-[#800000] mb-2 flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5" /> SO Margin Analysis (Informational)
                </p>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <p className="text-[10px] text-gray-500">Total SO Value</p>
                    <p className="text-sm font-bold">{formatCurrencyCompact((margin.total_so_value||0))}</p>
                    <p className="text-[10px] text-gray-400">{margin.so_count} orders</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Total Margin</p>
                    <p className="text-sm font-bold text-emerald-700">{formatCurrencyCompact((margin.total_margin||0))}</p>
                    <p className="text-[10px] text-emerald-600">{margin.avg_margin_pct}% avg</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Billing Rate</p>
                    <p className={`text-sm font-bold ${margin.billing_rate_pct >= 60 ? 'text-emerald-700' : margin.billing_rate_pct >= 40 ? 'text-amber-700' : 'text-red-600'}`}>
                      {margin.billing_rate_pct}%
                    </p>
                    <p className="text-[10px] text-gray-400">fully invoiced</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500">Pending Invoice</p>
                    <p className="text-sm font-bold text-amber-700">
                      {soMarginVec?.to_invoice_count || 0} SOs
                    </p>
                    <p className="text-[10px] text-gray-400">to invoice</p>
                  </div>
                </div>
                {/* Top salespersons by margin */}
                {soMarginVec?.top_salespersons?.length > 0 && (
                  <div className="mt-3 border-t pt-2">
                    <p className="text-[10px] font-semibold text-gray-500 mb-1.5">Top Salespersons by SO Margin</p>
                    <div className="space-y-1.5">
                      {soMarginVec.top_salespersons.slice(0, 5).map((sp, i) => {
                        const maxVal = soMarginVec.top_salespersons[0]?.so_value || 1;
                        const w = Math.round(sp.so_value / maxVal * 100);
                        return (
                          <div key={sp.salesperson} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-400 w-4">{i+1}</span>
                            <span className="flex-1 truncate">{sp.salesperson}</span>
                            <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-[#800000] rounded-full" style={{width:`${w}%`}} />
                            </div>
                            <span className="w-24 text-right font-mono">{formatCurrencyCompact(sp.so_value)}</span>
                            <span className={`w-12 text-right font-semibold ${sp.margin_pct>=50?'text-emerald-600':sp.margin_pct>=30?'text-amber-600':'text-red-600'}`}>
                              {sp.margin_pct}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {result.floor_triggered && (
                  <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700">
                    ⚠️ {result.floor_reason}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CreatePlanDialog({ open, onClose, onCreated, productManagers }) {
  const [form, setForm] = useState({ name: '', product_manager_name: '', product_manager_id: '', booking_target: 0, invoiced_target: 0, margin_target: 0, target_amount: 0, period: '2026-Q1', plan_type: 'revenue', assign_mode: 'individual', team_members: [] }); const [sub, setSub] = useState(false);
  const handlePM = (n) => { const pm = productManagers.find(p => p.name === n); setForm(f => ({ ...f, product_manager_name: n, product_manager_id: String(pm?.id || ''), name: `${f.period.split('-')[1]} ${f.period.split('-')[0]} - ${n}` })); };
  const handleAssignee = (name, mode, members) => {
    // Cross-reference productManagers list to preserve product_manager_id for revenue plans
    const pm = productManagers.find(p => p.name === name);
    setForm(f => ({
      ...f,
      product_manager_name: name,
      product_manager_id: pm ? String(pm.id) : f.product_manager_id,
      assign_mode: mode || 'individual',
      team_members: members || [],
      name: `${f.period.split('-')[1]} ${f.period.split('-')[0]} - ${name}${mode === 'team' ? ' (Team)' : ''}`,
    }));
  };
  const planTypeLabels = {
    revenue: 'Product Director (HOD or Individual)',
    strategy: 'Assignee (HOD or Individual)',
    marketing: 'Assignee (HOD or Individual)',
  };
  const planTypeHints = {
    revenue: 'Select a Product Director — if HOD detected, choose to assign to their team or individually',
    strategy: 'Strategy Team gets a parallel revenue target from CEO + assessment/workshop activities',
    marketing: 'Marketing gets campaign activities; PDs sponsor, marketing executes',
  };
  const submit = async () => { if (!form.product_manager_name || !form.booking_target) { toast.error('Select assignee and booking target'); return; } setSub(true); try { const res = await targetAPI.createRevenuePlan({...form, target_amount: form.booking_target}); toast.success('Plan created!'); onCreated(res.data); } catch (err) { const detail = err.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (typeof detail === 'string' ? detail : 'Failed'); toast.error(msg); } finally { setSub(false); } };
  return <Dialog open={open} onOpenChange={onClose}><DialogContent className="max-w-lg" data-testid="create-plan-dialog"><DialogHeader><DialogTitle>Assign Target</DialogTitle></DialogHeader><div className="space-y-3">
    <div><Label className="text-xs text-gray-500">Target Type</Label>
      <Select value={form.plan_type} onValueChange={v => setForm(f => ({ ...f, plan_type: v }))}>
        <SelectTrigger data-testid="select-plan-type"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="revenue">Product Director (Revenue)</SelectItem>
          <SelectItem value="strategy">Strategy Team (Revenue + Activities)</SelectItem>
          <SelectItem value="marketing">Marketing Team (Campaign Activities)</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div><Label className="text-xs text-gray-500">{planTypeLabels[form.plan_type] || 'Assignee'}</Label>
      {/* ALL plan types now use AssigneeInput with HOD/Individual detection */}
      <AssigneeInput value={form.product_manager_name} onChange={handleAssignee} teamType={form.plan_type} />
      {planTypeHints[form.plan_type] && (
        <p className={`text-[10px] mt-1 ${form.plan_type === 'marketing' ? 'text-blue-600' : form.plan_type === 'strategy' ? 'text-purple-600' : 'text-gray-400'}`}>
          {planTypeHints[form.plan_type]}
        </p>
      )}
    </div>
    <div className="grid grid-cols-3 gap-3"><div><Label className="text-xs text-gray-500">Booking Target (OMR)</Label><p className="text-[10px] text-gray-400 mb-1">{form.plan_type === 'strategy' ? 'Revenue from services' : form.plan_type === 'marketing' ? 'Lead pipeline value' : 'Won CRM deals'}</p><Input type="number" value={form.booking_target} onChange={e => setForm(f => ({ ...f, booking_target: parseFloat(e.target.value) || 0 }))} data-testid="booking-target-input" /></div><div><Label className="text-xs text-gray-500">Invoiced Target (OMR)</Label><p className="text-[10px] text-gray-400 mb-1">Paid invoices</p><Input type="number" value={form.invoiced_target} onChange={e => setForm(f => ({ ...f, invoiced_target: parseFloat(e.target.value) || 0 }))} data-testid="invoiced-target-input" /></div><div><Label className="text-xs text-gray-500">Margin Target (OMR)</Label><p className="text-[10px] text-gray-400 mb-1">Gross profit</p><Input type="number" value={form.margin_target} onChange={e => setForm(f => ({ ...f, margin_target: parseFloat(e.target.value) || 0 }))} data-testid="margin-target-input" /></div></div><div className="grid grid-cols-2 gap-3"><div><Label className="text-xs text-gray-500">Period</Label><Select value={form.period} onValueChange={v => setForm(f => ({...f, period: v, name: `${v.split('-')[1]} ${v.split('-')[0]} - ${f.product_manager_name || ''}`}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="2026-Q1">Q1 2026</SelectItem><SelectItem value="2026-Q2">Q2 2026</SelectItem><SelectItem value="2026-Q3">Q3 2026</SelectItem><SelectItem value="2026-Q4">Q4 2026</SelectItem></SelectContent></Select></div><div><Label className="text-xs text-gray-500">Plan Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div></div></div><DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={sub} className="bg-[#800000] hover:bg-[#9a1919] text-white" data-testid="submit-plan-btn">{sub ? '...' : 'Assign Target'}</Button></DialogFooter></DialogContent></Dialog>;
}

function AddItemDialog({ open, onClose, onCreated, planId, solutionCats, activityTypes }) {
  const [form, setForm] = useState({ activity_type: '', solution_category: '', target_count: 10, notes: '', assign_team: '', sponsor_pd: '' }); const [sub, setSub] = useState(false);
  const allActivityTypes = [
    ...activityTypes,
    ...[
      { type: 'Awareness Camp', count: 0 },
      { type: 'Assessment Services', count: 0 },
      { type: 'CEO Presentation', count: 0 },
      { type: 'Digital Campaign', count: 0 },
      { type: 'Event', count: 0 },
    ].filter(at => !activityTypes.find(a => a.type === at.type))
  ];
  const submit = async () => { if (!form.activity_type) { toast.error('Select type'); return; } setSub(true); try { await targetAPI.createPlanItem(planId, { ...form, assign_team: form.assign_team || null, sponsor_pd: form.sponsor_pd || null }); toast.success('Added'); onCreated(); } catch { toast.error('Failed'); } finally { setSub(false); } };
  return <Dialog open={open} onOpenChange={onClose}><DialogContent><DialogHeader><DialogTitle>Add Activity Plan Item</DialogTitle></DialogHeader><div className="space-y-3">
    <div><Label className="text-xs">Activity Type</Label><Select value={form.activity_type} onValueChange={v => setForm(f => ({ ...f, activity_type: v }))}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{allActivityTypes.map(at => <SelectItem key={at.type} value={at.type}>{at.type} {at.count > 0 ? `(${at.count})` : ''}</SelectItem>)}</SelectContent></Select></div>
    <div><Label className="text-xs">Solution Category</Label><Select value={form.solution_category || '_none'} onValueChange={v => setForm(f => ({ ...f, solution_category: v === '_none' ? '' : v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="_none">Any</SelectItem>{solutionCats.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select></div>
    <div><Label className="text-xs">Executing Team</Label><Select value={form.assign_team || '_sales'} onValueChange={v => setForm(f => ({ ...f, assign_team: v === '_sales' ? '' : v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="_sales">Sales Team</SelectItem><SelectItem value="marketing">Marketing Team (PD sponsors)</SelectItem><SelectItem value="strategy">Strategy GM Team (PD sponsors)</SelectItem></SelectContent></Select>
      {(form.assign_team === 'marketing' || form.assign_team === 'strategy') && <p className="text-[10px] text-gray-500 mt-1">PD sponsors this activity; {form.assign_team} team executes</p>}
    </div>
    <div><Label className="text-xs">Target Count</Label><Input type="number" value={form.target_count} onChange={e => setForm(f => ({ ...f, target_count: parseInt(e.target.value) || 0 }))} /></div>
    <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Thales DAM customer awareness roundtable Q2" /></div>
  </div><DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={sub} className="bg-[#800000] hover:bg-[#9a1919] text-white">{sub ? '...' : 'Add'}</Button></DialogFooter></DialogContent></Dialog>;
}

function RedistributeDialog({ open, onClose, onCreated, item }) {
  const [form, setForm] = useState({ assign_type: 'person', assigned_to_name: '', assigned_count: 0, team_name: '', notes: '' });
  const [sub, setSub] = useState(false);
  const remaining = (item.target_count || 0) - (item.redistributed_total || 0);

  const handleAssignee = (name, mode, members) => {
    setForm(f => ({
      ...f,
      assigned_to_name: name,
      assign_type: mode === 'team' ? 'team' : 'person',
      team_name: mode === 'team' ? name : '',
      notes: mode === 'team' && members?.length
        ? `${name}'s Team (${members.length} members): ${members.slice(0, 3).join(', ')}${members.length > 3 ? '...' : ''}`
        : f.notes
    }));
  };

  const submit = async () => {
    if (!form.assigned_to_name || !form.assigned_count) {
      toast.error('Select assignee and enter count');
      return;
    }
    setSub(true);
    try {
      await targetAPI.redistributePlanItem(item.id, {
        plan_item_id: item.id,
        assign_type: form.assign_type,
        assigned_to_name: form.assigned_to_name,
        assigned_count: form.assigned_count,
        team_name: form.team_name,
        notes: form.notes,
      });
      toast.success('Assigned successfully');
      onCreated();
    } catch { toast.error('Failed to assign'); }
    finally { setSub(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign: {item.target_count} {item.activity_type}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500">
          Assigned: {item.redistributed_total || 0} |{' '}
          <span className="text-orange-600 font-semibold">Remaining: {remaining}</span>
        </p>
        <div className="space-y-4">
          {/* Assignee — uses same HOD/Individual logic as CreatePlanDialog */}
          <div>
            <Label className="text-xs text-gray-600">Assignee (HOD or Individual)</Label>
            <div className="mt-1">
              <AssigneeInput value={form.assigned_to_name} onChange={handleAssignee} />
            </div>
          </div>

          {/* Count */}
          <div>
            <Label className="text-xs text-gray-600">Count (max {remaining})</Label>
            <Input
              type="number"
              min={1}
              max={remaining}
              value={form.assigned_count}
              onChange={e => setForm(f => ({ ...f, assigned_count: Math.min(parseInt(e.target.value) || 0, remaining) }))}
              placeholder={`0 – ${remaining}`}
              className="mt-1"
            />
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs text-gray-600">Notes</Label>
            <Input
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional context..."
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={sub || !form.assigned_to_name || !form.assigned_count}
            className="bg-[#800000] hover:bg-[#9a1919] text-white"
          >
            {sub ? 'Assigning...' : form.assign_type === 'team' ? 'Assign to Team' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CeoSummaryCard({ year }) {
  const [data, setData] = useState(null);
  useEffect(() => { targetAPI.getCeoSummary(year || new Date().getFullYear().toString()).then(r => setData(r.data)).catch(() => {}); }, [year]);
  if (!data) return null;

  const signalColors = { green: 'bg-emerald-500', amber: 'bg-yellow-500', red: 'bg-red-500' };
  const signalBg = { green: 'bg-emerald-50', amber: 'bg-yellow-50', red: 'bg-red-50' };
  const signalText = { green: 'text-emerald-700', amber: 'text-yellow-700', red: 'text-red-700' };

  return (
    <Card className={`border-2 ${data.overall === 'red' ? 'border-red-200 bg-red-50/30' : data.overall === 'amber' ? 'border-yellow-200 bg-yellow-50/30' : 'border-emerald-200 bg-emerald-50/30'}`} data-testid="ceo-summary">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900">Executive Summary</h3>
          <Badge variant="outline" className={`text-xs ${signalText[data.overall]} border-current`}>{data.red_count} Red · {data.amber_count} Amber</Badge>
        </div>
        <div className="grid grid-cols-5 gap-3 mb-3">
          {data.signals.map(s => (
            <div key={s.name} className={`p-2.5 rounded-lg ${signalBg[s.signal]} text-center`}>
              <div className={`w-3 h-3 rounded-full ${signalColors[s.signal]} mx-auto mb-1`} />
              <p className="text-xs font-medium text-gray-700">{s.name}</p>
              <p className={`text-lg font-bold ${signalText[s.signal]}`}>{s.value}</p>
              <p className="text-[10px] text-gray-500">{s.detail}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-600 italic">{data.insight}</p>
      </CardContent>
    </Card>
  );
}

function SuggestionsCard({ planId, onAccepted }) {
  const [data, setData] = useState(null);
  const [mods, setMods] = useState([]);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (planId) {
      targetAPI.getActivitySuggestions(planId).then(r => {
        if (r.data && r.data.status === 'pending_review') {
          setData(r.data);
          setMods(r.data.suggestions.map(s => ({ ...s })));
        }
      }).catch(() => {});
    }
  }, [planId]);

  if (!data || data.status !== 'pending_review') return null;

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await targetAPI.acceptSuggestions(planId, mods);
      toast.success('Activity plan accepted');
      setData(null);
      if (onAccepted) onAccepted();
    } catch { toast.error('Failed'); }
    finally { setAccepting(false); }
  };

  return (
    <Card className="border-2 border-blue-200 bg-blue-50/30" data-testid="suggestions-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
          <Target className="h-4 w-4" /> Suggested Activity Plan
          <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-300">Auto-generated</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.avg_deal_size > 0 && <p className="text-xs text-gray-600">
          Based on your historical data: Avg deal {formatCurrencyCompact(data.avg_deal_size || 0)}, Win rate {data.win_rate}%, {data.required_deals} deals needed.
          Pipeline coverage: {formatCurrencyCompact(data.pipeline_coverage || 0)} (3x target).
        </p>}
        {data.avg_deal_size === 0 && <p className="text-xs text-gray-600">
          AI-recommended activities based on solution categories and team type.
        </p>}
        <Table>
          <TableHeader><TableRow><TableHead>Activity</TableHead><TableHead>Category</TableHead><TableHead>Team</TableHead><TableHead>Formula</TableHead><TableHead className="text-right w-24">Count</TableHead></TableRow></TableHeader>
          <TableBody>
            {mods.map((s, idx) => {
              const teamLabel = s.assign_team === 'marketing' ? 'Marketing' : s.assign_team === 'strategy' ? 'Strategy' : 'Sales';
              const teamColor = s.assign_team === 'marketing' ? 'bg-blue-100 text-blue-700' : s.assign_team === 'strategy' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600';
              return (
              <TableRow key={idx}>
                <TableCell className="font-medium text-sm">{s.activity_type}</TableCell>
                <TableCell className="text-xs text-gray-500">{s.solution_category || '-'}</TableCell>
                <TableCell><Badge variant="secondary" className={`text-[9px] ${teamColor}`}>{teamLabel}</Badge></TableCell>
                <TableCell className="text-xs text-gray-500">{s.formula}</TableCell>
                <TableCell className="text-right">
                  <Input type="number" value={s.count} onChange={e => {
                    const newMods = [...mods];
                    newMods[idx] = { ...newMods[idx], count: parseInt(e.target.value) || 0 };
                    setMods(newMods);
                  }} className="w-20 h-7 text-xs text-right" />
                </TableCell>
              </TableRow>);
            })}
          </TableBody>
        </Table>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => setData(null)}>Dismiss</Button>
          <Button size="sm" className="bg-[#800000] hover:bg-[#9a1919] text-white text-xs" onClick={handleAccept} disabled={accepting}>
            {accepting ? 'Accepting...' : 'Accept & Create Plan Items'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
